<template>
  <form>
    <vl-form-column v-vl-spacer:bottom.small>
      <PbsSelectField
        name="fromEntity"
        :label="t('operationEntityDataMapping.detail.fromEntity')"
        :placeholder="$t('operationEntityDataMapping.detail.fromEntity')"
        :options="fromEntityOptions"
        :custom-label="fromEntityLabel"
      />
    </vl-form-column>
    <vl-form-column v-vl-spacer:bottom.small>
      <PbsSelectField
        name="toEntity"
        :label="t('operationEntityDataMapping.detail.toEntity')"
        :placeholder="$t('operationEntityDataMapping.detail.toEntity')"
        :options="toEntityOptions"
        :custom-label="toEntityLabel"
      />
    </vl-form-column>
    <vl-form-column v-if="canAddAttributeMapping" v-vl-spacer:bottom.small>
      <OperationEntityDataMappingAttributesEdit
        :definition-id="definitionId"
        :parent-definition-id="parentDefinitionId"
        :from-entity-id="fromEntityId"
        :to-entity-id="toEntityId"
      />
    </vl-form-column>
    <PbsErrorField name="custom-validation" />
  </form>
</template>

<script lang="ts" setup>
import { computed, onMounted, watch } from 'vue';
import { useForm } from 'vee-validate';
import { useI18n } from 'vue-i18n';
import { useOperationEntityDataMappingStore } from '@/modules/configuration/store/operation-entity-data-mapping/operation-entity-data-mapping.store';
import { OperationEntityDataMappingDetailDto } from '@/api/backoffice/clients';
import { useOperationEntityStore } from '@/modules/configuration/store/operation-entity/operation-entity.store';
import { OperationEntityDetailDto } from '@/api/backoffice/clients';
import OperationEntityDataMappingAttributesEdit from './OperationEntityDataMappingAttributesEdit.vue';
import { useOperationEntityDataMappingValidations } from './operation-entity-data-validations';
import PbsSelectField from '@/components/forms/PbsSelectField.vue';
import PbsErrorField from '@/components/forms/PbsErrorField.vue';

const { t } = useI18n();

const store = useOperationEntityDataMappingStore();
const operationEntityStore = useOperationEntityStore();

const props = defineProps<{
  detail: OperationEntityDataMappingDetailDto;
  definitionId: number;
  parentDefinitionId: number;
}>();

const { validationSchema } = useOperationEntityDataMappingValidations(t);

const { resetForm, setValues, handleSubmit, values } = useForm({
  validationSchema
});

const fromEntityOptions = computed(() => {
  return operationEntityStore.byDefinition(props.parentDefinitionId);
});

const toEntityOptions = computed(() => {
  return operationEntityStore.byDefinition(props.definitionId);
});

const fromEntityId = computed(() => {
  return values.fromEntity?.id ?? 0;
});

const toEntityId = computed(() => {
  return values.toEntity?.id ?? 0;
});

const attributeMappings = computed(() => {
  return values.attributeMappings ?? [];
});

const canAddAttributeMapping = computed(() => {
  return fromEntityId.value && toEntityId.value;
});

const fromEntityLabel = (operationEntity: OperationEntityDetailDto) => {
  return `${operationEntity.code} (${operationEntity.label})`;
};

const toEntityLabel = (operationEntity: OperationEntityDetailDto) => {
  return `${operationEntity.code} (${operationEntity.label})`;
};

watch(fromEntityId, (newValue, oldValue) => {
  if (newValue === oldValue) {
    return;
  }

  const fromEntity = fromEntityOptions.value.find((x) => x.id === newValue);
  if (fromEntity) {
    updateFromAttributeValues(fromEntity);
  }
});

watch(toEntityId, (newValue, oldValue) => {
  if (newValue === oldValue) {
    return;
  }

  const toEntity = toEntityOptions.value.find((x) => x.id === newValue);
  if (toEntity) {
    updateToAttributeValues(toEntity);
  }
});

onMounted(async () => {
  resetForm({
    values: getInitialFormValues()
  });

  await Promise.all([
    operationEntityStore.getByDefinition(props.parentDefinitionId),
    operationEntityStore.getByDefinition(props.definitionId)
  ]);
});

const updateFromAttributeValues = (fromEntity: OperationEntityDetailDto) => {
  const fromAttributes = fromEntity.model?.attributes;
  if (!fromAttributes) {
    return;
  }

  let shouldUpdate = false;
  const updatedAttributeMappings = attributeMappings.value.map((x) => {
    const mapping = { ...x };
    const fromAttribute = fromAttributes.find((x) => x.id === mapping.from?.id);
    if (!fromAttribute) {
      mapping.from = null;
      shouldUpdate = true;
    }
    return mapping;
  });

  if (shouldUpdate) {
    setValues(
      {
        ...values,
        attributeMappings: updatedAttributeMappings
      },
      true
    );
  }
};

const updateToAttributeValues = (toEntity: OperationEntityDetailDto) => {
  const toAttributes = toEntity.model?.attributes;
  if (!toAttributes) {
    return;
  }

  let shouldUpdate = false;
  const updatedAttributeMappings = attributeMappings.value.map((x) => {
    const mapping = { ...x };
    const toAttribute = toAttributes.find((x) => x.id === mapping.to?.id);
    if (!toAttribute) {
      mapping.to = null;
      shouldUpdate = true;
    }
    return mapping;
  });

  if (shouldUpdate) {
    setValues({
      ...values,
      attributeMappings: updatedAttributeMappings
    });
  }
};

const getInitialFormValues = () => {
  const values = {
    fromEntity: props.detail.fromEntity,
    toEntity: props.detail.toEntity,
    attributeMappings: getInitialAttributeMappings()
  };

  return values;
};

const getInitialAttributeMappings = () => {
  return props.detail.attributeMappings
    .map((x) => ({
      id: x.id,
      from: x.fromAttribute,
      to: x.toAttribute
    }))
    .sort((x, y) => (x.from.code > y.from.code ? 1 : -1));
};

const createAttributeMappingsToUpdate = () => {
  return values.attributeMappings.map((x) => ({
    id: x.id,
    fromAttributeId: x.from.id,
    toAttributeId: x.to.id
  }));
};

const onSuccess = async (values) => {
  const attributeMappings = createAttributeMappingsToUpdate();

  const updateDto = {
    deliveryProcessDefinitionId: props.definitionId,
    fromEntityId: values.fromEntity ? values.fromEntity.id : null,
    toEntityId: values.toEntity ? values.toEntity.id : null,
    attributeMappings
  };

  await store.update(props.definitionId, props.detail.id, updateDto);
};

const onInvalidSubmit = ({ values, errors, results }) => {
  // console.log(values); // current form values
  // console.log(errors); // a map of field names and their first error message
  // console.log(results); // a detailed map of field names and their validation results
};

const onSubmit = handleSubmit(onSuccess, onInvalidSubmit);

defineExpose({ onSubmit });
</script>
